import { PayloadAction } from '@reduxjs/toolkit';
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import * as AnalyticsApp from './analytics';
import * as FirebaseApp from './app';
import AttributionService, { IUrlParams } from './attributionService';
import * as GdprService from './gdprService';
import IterableService from './iterableService';
import * as MessaginApp from './messaging';
import outbrainService from './outbrainService';
import * as RemoteConfigApp from './remoteConfig';
import * as Selectors from './selectors';
import {
  FirebaseActions,
  IFirebaseInitPayload,
  IFirebaseStartPayload,
  ISetPropertyPayload,
  ISetUserPayload,
  IUpdateFirebaseConsentPayload,
} from './slice';
import taboolaService from './taboolaService';
import { EBannerMode, ICookieConsent, IEventActionPayload } from './types';

export function* initializeFirebase(action: PayloadAction<IFirebaseInitPayload>) {
  const { apiKey, appId, measurementId, messagingSenderId, projectId, searchParams } = action.payload;
  yield call(FirebaseApp.init, apiKey, projectId, appId, messagingSenderId, measurementId);
  // Initializing GDPR
  yield call(detectUTMTracking, searchParams);
  yield call(detectBannerMode);
  const taboolaId: string | undefined = yield select(Selectors.getTaboolaId);
  const outbrainId: string | undefined = yield select(Selectors.getOutbrainId);
  yield put(FirebaseActions.initTaboola(taboolaId));
  yield put(FirebaseActions.initOutbrain(outbrainId));
  const consent: ICookieConsent | undefined = yield call(GdprService.getConsent);
  if (consent) {
    yield put(FirebaseActions.updateConsent(consent));
  }
  yield put(FirebaseActions.firebaseInitialized());
}

export function* startFirebaseApps(action: PayloadAction<IFirebaseStartPayload>) {
  const options = action.payload;
  // wait for firebase core app to be initalized
  const isInitialized: boolean = yield select(Selectors.isInitialized);
  if (!isInitialized) {
    yield take(FirebaseActions.firebaseInitialized.type);
  }
  const sessionId: string = yield select(Selectors.getSessionId);
  const { analytics, config, messaging } = options;
  if (analytics) {
    yield call(AnalyticsApp.initAnalytics, sessionId);
  }
  let configReady = false;
  if (config) {
    yield call(RemoteConfigApp.init);
    const fetchConfig: boolean | undefined = yield call(RemoteConfigApp.fetchConfig);
    if (fetchConfig !== undefined) configReady = true;
  }
  if (messaging) {
    yield call(MessaginApp.init);
  }
  yield put(FirebaseActions.firebaseReady(!!analytics, configReady, !!messaging));
}

export function* waitToBeReady() {
  const ready: boolean = yield select(Selectors.isReady);
  if (!ready) {
    yield take(FirebaseActions.firebaseReady.type);
  }
}

export function* configureTracking(action: PayloadAction<IUpdateFirebaseConsentPayload>) {
  const { analytics, userData } = action.payload;
  yield call(waitToBeReady);
  yield call(AnalyticsApp.setCookieConsentProperty, analytics);
  yield call(AnalyticsApp.updateConsent, analytics, userData);
}

// fire custom event
export function* sendAnalyticsEvent(action: PayloadAction<IEventActionPayload>) {
  const { payload: analyticsEvent } = action;
  yield call(waitToBeReady);
  yield call(AnalyticsApp.analyticsEvent, analyticsEvent);
}

export function* setUserSaga(action: PayloadAction<ISetUserPayload>) {
  const { userId, email } = action.payload;
  yield call(waitToBeReady);
  // set user id in Firebase
  yield call(AnalyticsApp.setAnalyticsUserId, userId);
  const messagingEnabled: boolean = yield select(Selectors.isMessagingActive);
  if (!messagingEnabled) {
    // skip iterable if messagin was not turned on
    return;
  }
  yield call(MessaginApp.setUserId, userId);
  if (!email) {
    throw new Error('Email of the user must be specified for Messaging app to work!');
  }
  yield call(IterableService.init, ApiService, userId, email);
}

export function* initTaboolaSaga(action: PayloadAction<string | undefined>) {
  const taboolaClickId = action.payload;
  // configure taboola service with api service click id
  yield call(taboolaService.init, ApiService, taboolaClickId);
}

export function* sendTaboolaSubscribeSaga() {
  yield call(taboolaService.trackSubscribe);
}

export function* sendTaboolaValidationSaga() {
  try {
    yield call(taboolaService.trackValidation);
  } catch (error) {
    console.log(error);
  }
}

export function* initOutbrainServiceSaga(action: PayloadAction<string | undefined>) {
  const outbrainClickId = action.payload;
  // configure outbrain service
  yield call(outbrainService.init, ApiService, outbrainClickId);
}

export function* sendOutbrainSubscribeEventSaga() {
  yield call(outbrainService.trackSubscribe);
}

export function* sendOutbrainValidationSaga() {
  try {
    yield call(outbrainService.trackValidation);
  } catch (error) {
    console.log(error);
  }
}

// set user property
export function* setUserProperty(action: PayloadAction<ISetPropertyPayload>) {
  yield call(waitToBeReady);
  const { name, value } = action.payload;
  yield call(AnalyticsApp.setProperty, name, value);
}

export function* requestPushPermission(action: PayloadAction<string>) {
  const vapidKey = action.payload;
  try {
    const { token }: { token?: string; timeout?: boolean } = yield race({
      token: call(MessaginApp.getPushToken, vapidKey),
      // we wait for some delay because Edge browser does not reject promise when permission is blocked
      timeout: delay(15000),
    });
    if (!token) {
      // if browser does not support PUSH getPushToken will return undefined
      yield put(FirebaseActions.pushPermissionResult(false, 'Push API not supported!', true));
      return;
    }
    // token received
    yield call(savePushTokenToIterable, token);
  } catch {
    try {
      // try to get token, in case request failed due to firebase bug
      const token: string | undefined = yield call(MessaginApp.getPushToken, vapidKey);
      if (!token) {
        // if browser does not support PUSH getPushToken will return undefined
        yield put(FirebaseActions.pushPermissionResult(false, 'Push API not supported!', true));
        return;
      }
      // token received
      yield call(savePushTokenToIterable, token);
      return;
    } catch {
      // this error we ignore
    }
    // token does not exist - permission was blocked
    yield put(FirebaseActions.pushPermissionResult(false, 'Permission not granted!'));
  }
}

export function* savePushTokenToIterable(token: string) {
  try {
    yield call(IterableService.registerPushToken, token);
  } catch {
    yield put(FirebaseActions.pushPermissionResult(false, 'Failed to register token!'));
    return;
  }
  yield put(FirebaseActions.pushPermissionResult(true));
}

export function* enableMessagingSaga(action: PayloadAction<string>) {
  const vapidKey = action.payload;
  yield call(waitToBeReady);
  yield put(FirebaseActions.requestPushPermission(vapidKey));
  const result: ReturnType<typeof FirebaseActions.pushPermissionResult> = yield take(
    FirebaseActions.pushPermissionResult.type
  );
  if (!result.payload.permission) {
    yield call(console.warn, ['Failed to set up messaging!', result.payload.reason]);
    return;
  }
  yield put(FirebaseActions.messagingEnabled());
}

export function* detectUTMTracking(searchParams: URLSearchParams) {
  // restore parameters set before
  yield call(AttributionService.restoreParams);
  // parse current url and update parameters in storage
  yield call(AttributionService.parseParamsFromUrl, searchParams);
  const utmParams: IUrlParams = yield call(AttributionService.getParams);
  // update data in state
  yield put(FirebaseActions.updateUtmTracking(utmParams));
}

export function* detectBannerMode() {
  // detectUTMTracking must be run before this function
  const cks: string | undefined = yield select(Selectors.getCks);
  if (typeof cks === 'undefined') {
    // do nothing if parameter not set
    return;
  }
  const mode: EBannerMode = Number(cks);
  yield put(FirebaseActions.updateBannerMode(mode));
}

export function* useConsentSaga(action: PayloadAction<ICookieConsent>) {
  const consent = action.payload;
  const { firebase, userData } = consent;
  yield put(FirebaseActions.updateFirebaseConsent({ analytics: firebase, userData }));
}

export function* saveUserConsentSaga(action: PayloadAction<ICookieConsent>) {
  const consent = action.payload;
  yield call(GdprService.saveConsent, consent);
  yield put(FirebaseActions.updateConsent(consent));
}

export function* requestExportSaga() {
  try {
    yield call(GdprService.GDPRHttpService.requestGDPRExport);
    yield put(FirebaseActions.requestExportSuccess());
  } catch {
    yield put(FirebaseActions.requestExportFailure());
  }
}

export function* initGdprExportSaga() {
  yield call(GdprService.GDPRHttpService.init, ApiService);
}

export function* handlePushMessageClickSaga(action: PayloadAction<string>) {
  const { payload: messageId } = action;
  // try to create a detached task to prevent cancellation on redirects
  yield spawn(IterableService.trackPushClick, messageId);
}

// base watcher for Firebase actions
export default function* watchFirebase() {
  yield all([
    takeLatest(FirebaseActions.firebaseInit.type, initializeFirebase),
    takeLatest(FirebaseActions.firebaseStart.type, startFirebaseApps),
    takeEvery(FirebaseActions.updateFirebaseConsent.type, configureTracking),
    takeEvery(FirebaseActions.analyticsEvent.type, sendAnalyticsEvent),
    takeLatest(FirebaseActions.setUser.type, setUserSaga),
    takeEvery(FirebaseActions.initTaboola.type, initTaboolaSaga),
    takeEvery(FirebaseActions.taboolaSubscribeEvent.type, sendTaboolaSubscribeSaga),
    takeEvery(FirebaseActions.taboolaValidationEvent.type, sendTaboolaValidationSaga),
    takeEvery(FirebaseActions.initOutbrain.type, initOutbrainServiceSaga),
    takeEvery(FirebaseActions.outbrainSubscribeEvent.type, sendOutbrainSubscribeEventSaga),
    takeEvery(FirebaseActions.outbrainValidationEvent.type, sendOutbrainValidationSaga),
    takeEvery(FirebaseActions.setUserProperty.type, setUserProperty),
    takeLeading(FirebaseActions.requestPushPermission.type, requestPushPermission),
    takeEvery(FirebaseActions.enableMessaging.type, enableMessagingSaga),
    takeEvery(FirebaseActions.updateConsent.type, useConsentSaga),
    takeEvery(FirebaseActions.saveConsent.type, saveUserConsentSaga),
    takeLeading(FirebaseActions.requestExport.type, requestExportSaga),
    takeEvery(FirebaseActions.initGdprExport.type, initGdprExportSaga),
    takeEvery(FirebaseActions.messageClicked.type, handlePushMessageClickSaga),
  ]);
}
